import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { paymentAPI } from '../services/api';
import CashfreePayment from '../components/CashfreePayment';
import Footer from '../components/Footer';
import { isEventBookingEnabled } from '../utils/eventBookingConfig';

const EventBookingReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [bookingIdForPayment, setBookingIdForPayment] = useState(null);

  // Get event ID from URL params or location state
  const eventId = id || location.state?.event?.id;

  // Get data from location state or use defaults
  useEffect(() => {
    console.log('EventBookingReview useEffect - location.state:', location.state);
    console.log('EventBookingReview useEffect - location.state?.bookingData:', location.state?.bookingData);
    
    if (location.state?.bookingData) {
      console.log('EventBookingReview received data:', location.state.bookingData);
      console.log('EventBookingReview - selectedTickets:', location.state.bookingData.selectedTickets);
      console.log('EventBookingReview - pricing:', location.state.bookingData.pricing);
      console.log('EventBookingReview - eventDetails:', location.state.bookingData.eventDetails);
      console.log('EventBookingReview - showDetails:', location.state.bookingData.showDetails);
      
      // Debug quantity calculations
      if (location.state.bookingData.selectedTickets) {
        location.state.bookingData.selectedTickets.forEach((ticket, index) => {
          console.log(`Ticket ${index + 1}:`, {
            name: ticket.name,
            quantity: ticket.quantity,
            price: ticket.price,
            taxes: ticket.taxes,
            ticketTotal: (ticket.price || 0) * (ticket.quantity || 1)
          });
        });
      }

      setBookingData(location.state.bookingData);
    } else {
      // Fallback to default data if no state
      console.log('No event booking data in state, using fallback data');
      setBookingData({
        eventDetails: {
          id: eventId || "1",
          title: "The Great Mumbai Pet Show",
          category: "show",
          location: "Mumbai",
          venue: "Exhibition Center, BKC",
          image: "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop"
        },
        showDetails: {
          date: "Wed, 30 Oct",
          time: "6:30 PM",
          duration: "2 hours"
        },
        selectedTickets: [
          {
            id: "ticket-1",
            name: "General Admission",
            quantity: 2,
            price: 1299,
            taxes: 234,
            description: "Standard entry ticket"
          }
        ],
        pricing: {
          subTotal: 2598,
          taxes: 468,
          total: 2598
        },
        user: {
          name: user?.name || "Guest User",
          email: user?.email || "guest@example.com",
          mobile: user?.mobile || "9876543210"
        }
      });
    }
  }, [location.state, eventId, user]);

  const handleBack = () => {
    // Check if we came from TicketSelection and navigate accordingly
    if (location.state?.fromTicketSelection) {
      navigate('/ticket-selection', { 
        state: { 
          event: location.state?.eventDetails || bookingData?.eventDetails 
        } 
      });
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  const handlePayNow = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to continue with payment');
      navigate('/');
      return;
    }

    if (!bookingData?.selectedTickets?.length) {
      alert('Please select at least one ticket.');
      return;
    }

    const evtId = bookingData.eventDetails?.id || eventId;
    const showId = bookingData.showDetails?.id;
    if (!evtId || !showId) {
      alert('Missing event or show information. Please go back and select a show again.');
      return;
    }

    const nameParts = (user.name || '').trim().split(/\s+/).filter(Boolean);
    const guestFirst = nameParts[0] || 'Guest';
    const guestLast = nameParts.slice(1).join(' ');
    const guestName = `${guestFirst} ${guestLast}`.trim();
    const guestEmail = (user.email || '').trim();
    const mobileDigits = String(user.mobile || '').replace(/\D/g, '').slice(-10);

    if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      alert('Please update your account with a valid email address before paying.');
      return;
    }
    if (mobileDigits.length !== 10) {
      alert('Please update your account with a valid 10-digit Indian mobile number before paying.');
      return;
    }

    try {
      setIsProcessingPayment(true);
      setPaymentError(null);

      const payload = {
        eventId: evtId,
        showId,
        guestInfo: {
          name: guestName,
          email: guestEmail,
          mobile: `+91${mobileDigits}`
        },
        tickets: bookingData.selectedTickets.map((t) => ({
          ticketTypeId: t.id,
          quantity: t.quantity || 1
        }))
      };

      const paymentResponse = await paymentAPI.initiateEventPayment(payload);

      if (paymentResponse.success && paymentResponse.paymentSessionId) {
        setPaymentSessionId(paymentResponse.paymentSessionId);
        setOrderId(paymentResponse.orderId);
        setBookingIdForPayment(paymentResponse.bookingId);
      } else {
        throw new Error('Failed to initialize payment session');
      }
    } catch (error) {
      console.error('Event payment initiation error:', error);
      setPaymentError(error.message || 'Failed to initiate payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = () => {
    setIsProcessingPayment(false);
    navigate('/event-booking-confirmation', {
      state: {
        bookingData: {
          ...bookingData,
          bookingId: bookingIdForPayment,
          orderId,
          paymentSuccess: true
        },
        fromEventBookingReview: true,
        eventId,
        orderId
      }
    });
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    setPaymentError(error.message || 'Payment failed. Please try again.');
    setIsProcessingPayment(false);
    setPaymentSessionId(null);
  };

  const handlePaymentClose = () => {
    setPaymentSessionId(null);
    setIsProcessingPayment(false);
    setPaymentError(null);
  };

  // Helper function to get user display name
  const getUserDisplayName = () => {
    if (bookingData?.user?.name) {
      return bookingData.user.name;
    }
    return user?.name || 'User';
  };

  // Helper function to get user email
  const getUserEmail = () => {
    return bookingData?.user?.email || user?.email || 'Email not provided';
  };

  // Helper function to get user mobile
  const getUserMobile = () => {
    return bookingData?.user?.mobile || user?.mobile || 'Mobile not provided';
  };

  // Helper function to split name into first and last name
  const getUserFirstName = () => {
    const name = getUserDisplayName();
    return name.split(' ')[0] || '';
  };

  const getUserLastName = () => {
    const name = getUserDisplayName();
    return name.split(' ').slice(1).join(' ') || '';
  };

  // Helper function to get total tickets count
  const getTotalTicketsCount = () => {
    if (bookingData?.selectedTickets) {
      return bookingData.selectedTickets.reduce((total, ticket) => total + (ticket.quantity || 1), 0);
    }
    return 1;
  };

  if (!isEventBookingEnabled()) {
    const redirectEventId =
      id || location.state?.event?.id || location.state?.event?.eventId;
    return (
      <Navigate to={redirectEventId ? `/event/${redirectEventId}` : '/events'} replace />
    );
  }

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] md:py-[60px]">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 bg-black text-white p-4 text-xs rounded max-w-md max-h-96 overflow-auto">
          <div className="font-bold mb-2">EventBookingReview Debug:</div>
          <div>Has location.state: {location.state ? 'Yes' : 'No'}</div>
          <div>Has bookingData: {location.state?.bookingData ? 'Yes' : 'No'}</div>
          <div>Current bookingData: {bookingData ? 'Set' : 'Null'}</div>
          <div>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</div>
          <div>User: {user?.name || 'Not logged in'}</div>
          {location.state && (
            <div className="mt-2 pt-2 border-t border-gray-600">
              <div>State keys: {Object.keys(location.state).join(', ')}</div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="p-[16px] max-w-7xl mx-auto">
        <div className="flex items-center justify-start gap-[16px]">
          <button
            onClick={handleBack}
            className=""
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_2846_6264)">
                <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2846_6264">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <h1 className="text-[20px] font-medium">Booking Review</h1>
        </div>
      </div>

      {/* Two Column Layout - Desktop */}
      <div className="px-[16px] pb-[100px] mt-[20px]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-[20px]">
          {/* Left Column - Booking Summary */}
          <div className="space-y-[20px]">
            {/* Event Details Card */}
            <div className="bg-white rounded-[16px] overflow-hidden shadow-sm flex p-[14px] gap-[8px]">
              <img
                src={bookingData.eventDetails?.image || "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop"}
                alt={bookingData.eventDetails?.title || "Event"}
                className="w-[100px] h-[100px] rounded-[12px] object-cover"
              />
              <div className="py-[12px]">
                <h3 className="font-medium text-[18px] text-black leading-[1.1] mb-[8px]">
                  {bookingData.eventDetails?.title || "Event Name"}
                </h3>
                <p className="text-[14px] text-[#78736A] leading-[1.1]">
                  {bookingData.eventDetails?.venue || "Venue not specified"}, {bookingData.eventDetails?.location || "Location"}
                </p>
              </div>
            </div>

            {/* Fare Summary Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Fare Summary</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="space-y-[20px]">
                {/* Show breakdown for each selected ticket */}
                {bookingData.selectedTickets && bookingData.selectedTickets.length > 0 ? (
                  <>
                    {bookingData.selectedTickets.map((ticket, index) => {
                      const ticketPrice = (ticket.price || 0) * (ticket.quantity || 1);
                      const ticketTaxes = (ticket.taxes || 0) * (ticket.quantity || 1);
                      return (
                        <div key={ticket.id || index}>
                          <div className="flex justify-between">
                            <span className="text-[#7E7E7E] text-[16px] leading-none">
                              {ticket.quantity || 1} x {ticket.name}
                            </span>
                            <span className="font-medium text-[16px] leading-none text-[#636363]">
                              ₹{ticketPrice.toLocaleString()}
                            </span>
                          </div>
                          {ticketTaxes > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[#7E7E7E] text-[16px] leading-none">
                                Included Taxes & Charges
                              </span>
                              <span className="font-medium text-[16px] leading-none text-[#636363]">
                                ₹{ticketTaxes.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  // Fallback to single ticket display
                  <>
                    <div className="flex justify-between">
                      <span className="text-[#7E7E7E] text-[16px] leading-none">
                        {getTotalTicketsCount()} Ticket{getTotalTicketsCount() > 1 ? 's' : ''}
                      </span>
                      <span className="font-medium text-[16px] leading-none text-[#636363]">
                        ₹{bookingData.pricing?.subTotal?.toLocaleString() || "0"}
                      </span>
                    </div>
                    {bookingData.pricing?.taxes > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[#7E7E7E] text-[16px] leading-none">Included Taxes & Charges</span>
                        <span className="font-medium text-[16px] leading-none text-[#636363]">
                          ₹{bookingData.pricing?.taxes?.toLocaleString() || "0"}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {bookingData.pricing?.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#7E7E7E] text-[16px] leading-none">Discounts</span>
                    <span className="text-[#2AA62B] text-[16px] leading-none font-medium">
                      ₹{bookingData.pricing.discount.toLocaleString()}
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-[#7E7E7E] text-[16px] leading-none">Total Payable</span>
                  <span className="font-medium text-[20px] leading-none text-[#000]">
                    ₹{(() => {
                      if (bookingData.selectedTickets && bookingData.selectedTickets.length > 0) {
                        // Calculate total from selected tickets with quantities
                        return bookingData.selectedTickets.reduce((total, ticket) => {
                          const ticketTotal = (ticket.price || 0) * (ticket.quantity || 1);
                          return total + ticketTotal;
                        }, 0).toLocaleString();
                      } else {
                        // Fallback to pricing object
                        return (bookingData.pricing?.total || 0).toLocaleString();
                      }
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Traveller and Event Details */}
          <div className="space-y-[20px] md:col-span-2">
            {/* Traveller Details Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Traveller Details</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="space-y-[20px]">
                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">First Name</label>
                  <input
                    type="text"
                    value={getUserFirstName()}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Last Name</label>
                  <input
                    type="text"
                    value={getUserLastName()}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Email Address</label>
                  <input
                    type="email"
                    value={getUserEmail()}
                    className="w-full p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-[16px] leading-none font-normal text-[#9d9d9d] tracking-[-0.8px] mb-[8px]">Mobile No</label>
                  <div className="flex space-x-2">
                    <div className="flex items-center p-[16px] border border-[#d9d9d9] rounded-[14px] min-w-[80px]">
                      <span className="mr-[8px]">🇮🇳</span>
                      <span className="text-black">+91</span>
                    </div>
                    <input
                      type="tel"
                      value={getUserMobile()}
                      className="flex-1 p-[16px] text-[16px] leading-none font-normal text-[#000] border border-[#d9d9d9] rounded-[14px] focus:outline-none focus:ring-[1px] focus:ring-[#d9d9d9] focus:border-transparent"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Event Details Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <h3 className="font-medium text-[18px] text-black leading-none mb-[20px]">Event Details</h3>
              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>
              <div className="flex items-center mb-[20px] justify-between">
                {/* Event Date */}
                <div className="flex flex-col gap-[6px] items-start">
                  <div className="flex items-center space-x-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <g clipPath="url(#clip0_2846_6297)">
                        <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_6297">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="text-[12px] text-[#999999]">Event Date</span>
                  </div>
                  <div>
                    <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.showDetails?.date || "Event date"}</div>
                    <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.showDetails?.time || "Event time"}</div>
                  </div>
                </div>

                {/* Duration */}
                <div className="text-center">
                  <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">{bookingData.showDetails?.duration || "Duration"}</span>
                </div>

                {/* Show Time */}
                <div className="flex flex-col gap-[6px] items-end">
                  <div className="flex items-center space-x-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <g clipPath="url(#clip0_2846_6297)">
                        <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.33203 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 1.75V4.08333" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M2.33203 6.41675H11.6654" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" strokeWidth="0.583333" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_6297">
                          <rect width="14" height="14" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="text-[12px] text-[#999999]">Show Time</span>
                  </div>
                  <div className="text-end">
                    <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.showDetails?.time || "Event time"}</div>
                    <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.showDetails?.duration || "Duration"}</div>
                  </div>
                </div>
              </div>

              <div className='flex items-center justify-center mb-[20px]'>
                <svg width="100%" height="1px">
                  <line
                    x1="0" y1="1" x2="100%" y2="1"
                    stroke="#f0f0f0"
                    strokeWidth="2"
                    strokeDasharray="10 4"
                  />
                </svg>
              </div>

              {/* Ticket Summary */}
              <div className="space-y-4">
                {bookingData.selectedTickets && bookingData.selectedTickets.length > 0 ? (
                  bookingData.selectedTickets.map((ticket, index) => (
                    <div key={ticket.id || index} className="flex items-center">
                      <div className="flex items-start space-x-[8px] w-full">
                        <div className='pt-[6px] pb-[12px] flex-1'>
                          <div className="font-medium leading-none mb-[6px] text-[18px]">
                            {ticket.quantity || 1} x {ticket.name || "Ticket"}
                          </div>
                          {ticket.description && (
                            <div className="text-[14px] text-[#78736A] leading-none">
                              {ticket.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center">
                    <div className="font-medium leading-none text-[18px]">
                      {getTotalTicketsCount()} x Ticket
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation Policy Card */}
            <div className="bg-white rounded-[16px] p-[20px] shadow-sm">
              <button
                onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
                className="w-full flex items-center justify-between"
              >
                <span className="font-medium text-[18px] text-black leading-none">Cancellation Policy</span>
                <ChevronDown
                  size={20}
                  className={`text-gray-500 transition-transform ${showCancellationPolicy ? 'rotate-180' : ''
                    }`}
                />
              </button>

              {showCancellationPolicy && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Free cancellation until 24 hours before the event. Cancellations made within 24 hours of the event will be charged 50% of the total booking amount.
                  </p>
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center justify-between w-full">
              <div className="flex items-end gap-[10px]">
                <div className="text-[14px] text-black leading-none ">Total Amount</div>
                <div className="flex items-end gap-[10px]">
                  <span className="text-[20px] font-bold text-black leading-none">
                    ₹{(() => {
                      if (bookingData.selectedTickets && bookingData.selectedTickets.length > 0) {
                        // Calculate total from selected tickets with quantities
                        return bookingData.selectedTickets.reduce((total, ticket) => {
                          const ticketTotal = (ticket.price || 0) * (ticket.quantity || 1);
                          return total + ticketTotal;
                        }, 0).toLocaleString();
                      } else {
                        // Fallback to pricing object
                        return (bookingData.pricing?.total || 0).toLocaleString();
                      }
                    })()}
                  </span>
                </div>
              </div>

              {/* Desktop Payment Button - Below Cancellation Policy */}
              <div className="">
                <button
                  onClick={handlePayNow}
                  disabled={isProcessingPayment}
                  className="bg-black w-[210px] text-white text-[16px] h-[56px] leading-[1.1] px-[40px] py-[14px] rounded-[14px] font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessingPayment ? 'Processing...' : 'Pay now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Payment Bar - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-[16px] md:hidden">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-[14px] text-black leading-none mb-[4px]">Total Amount</div>
            <div className="flex items-center gap-[8px]">
              <span className="text-[20px] font-bold text-black leading-none">
                ₹{(() => {
                  if (bookingData.selectedTickets && bookingData.selectedTickets.length > 0) {
                    // Calculate total from selected tickets with quantities
                    return bookingData.selectedTickets.reduce((total, ticket) => {
                      const ticketTotal = (ticket.price || 0) * (ticket.quantity || 1);
                      return total + ticketTotal;
                    }, 0).toLocaleString();
                  } else {
                    // Fallback to pricing object
                    return (bookingData.pricing?.total || 0).toLocaleString();
                  }
                })()}
              </span>
            </div>
          </div>
          <button
            onClick={handlePayNow}
            disabled={isProcessingPayment}
            className="bg-black text-white text-[16px] h-[56px] leading-[1.1] px-[40px] py-[14px] rounded-[14px] font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessingPayment ? 'Processing...' : 'Pay now'}
          </button>
        </div>
      </div>

      {/* Bottom spacing to prevent content from being hidden behind fixed bar - Mobile Only */}
      <div className="h-24 md:h-0"></div>

      {/* Footer - Desktop Only */}
      <div className="hidden md:block">
        <Footer />
      </div>

      {paymentError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex justify-between items-center">
            <span>{paymentError}</span>
            <button
              type="button"
              onClick={() => setPaymentError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {paymentSessionId && (
        <CashfreePayment
          paymentSessionId={paymentSessionId}
          orderId={orderId}
          returnPath="/event-booking-confirmation"
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          onClose={handlePaymentClose}
        />
      )}

      {/* Debug info in development mode */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-28 right-4 bg-black text-white p-4 text-xs rounded max-w-xs overflow-auto max-h-96">
          <h4 className="font-bold mb-2">Debug Info:</h4>
          <div>Event: {bookingData?.eventDetails?.title}</div>
          <div>Selected Tickets: {bookingData?.selectedTickets?.length || 0}</div>
          <div>Total Price: ₹{bookingData?.pricing?.total?.toLocaleString() || "0"}</div>
          <div>User: {bookingData?.user?.name || "N/A"}</div>
          <div className="mt-2">
            <strong>Selected Tickets:</strong>
            {bookingData?.selectedTickets?.map((ticket, idx) => (
              <div key={idx} className="ml-2">
                {ticket.quantity}x {ticket.name} - ₹{ticket.price}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventBookingReview;
